import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Camera, CloudOff, Gauge, Image as ImageIcon, TriangleAlert } from 'lucide-react';
import { getErrorMessage, hasProblemType, keys, portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate, formatDateTime, formatNumber, meterReadingSchema, parseMeterValue } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  CheckboxField,
  EmptyState,
  ErrorState,
  Form,
  FormRootError,
  InputField,
  Label,
  SearchInput,
  Skeleton,
  StatusBadge,
  ToggleGroup,
  toast,
} from '@blimo/ui';
import { useMeterQueue } from '../lib/meter-queue-context.js';
import { isNetworkError, newClientUuid } from '../lib/offline-queue.js';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ItemIcon } from '../components/ui/section.jsx';
import { ResponsiveSheet } from '../components/ui/responsive-sheet.jsx';

const meterValue = (value) => formatNumber(value, { maximumFractionDigits: 3 });
const FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: '1', label: 'Nur offene' },
];

/** Search over the loaded list (the API has no search parameter). */
function matches(group, reading, needle) {
  if (!needle) return true;
  return [reading.meter?.number, reading.meter?.type, group.property?.name, group.property?.address].filter(Boolean).join(' ').toLowerCase().includes(needle);
}

/** Zählerablesung (F-910): eigene Zähler je Liegenschaft, Erfassung mit Fotos, Offline-Warteschlange. */
export function MeterReadingsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const { queue, entries } = useMeterQueue();
  const query = useQuery(portalQueries.meterReadings(api));
  const setSearch = (patch) =>
    navigate({
      to: '/zaehlerablesung',
      search: (prev) => Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, v]) => v !== undefined && v !== '')),
      replace: true,
    });

  // Opening the page (after login) is a good moment to retry stored submissions.
  useEffect(() => {
    queue.flush().catch(() => {});
  }, [queue]);

  const needle = (search.q ?? '').trim().toLowerCase();
  const onlyOpen = search.open === 1;
  const groups = (query.data?.data ?? [])
    .map((group) => ({
      ...group,
      readings: (group.readings ?? []).filter((r) => (!onlyOpen || r.status === 'open') && matches(group, r, needle)),
    }))
    .filter((group) => group.readings.length > 0);
  const pending = entries.filter((e) => !e.error).length;
  const meta = query.data?.meta;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Zählerablesung"
        compactTitle="Zähler"
        description={
          meta ? (
            <span>
              Ablesejahr <span className="numeric">{meta.year}</span> · <span className="numeric">{formatNumber(meta.open)}</span> offen
            </span>
          ) : (
            'Ihre abzulesenden Zähler.'
          )
        }
      />
      {pending > 0 && (
        <p
          role="status"
          className="flex items-center gap-3 rounded-xl border border-warning/40 bg-warning-subtle px-4 py-2.5 text-[0.8125rem] font-medium text-warning-subtle-foreground"
        >
          <CloudOff className="size-4 shrink-0" aria-hidden="true" />
          {pending === 1 ? '1 Erfassung wartet' : `${pending} Erfassungen warten`} auf eine Verbindung und wird automatisch übertragen.
        </p>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <SearchInput
          label="Zähler suchen"
          placeholder="Zählernummer, Art oder Liegenschaft …"
          value={search.q ?? ''}
          onValueChange={(q) => setSearch({ q: q || undefined })}
          wrapperClassName="sm:w-80"
        />
        <ToggleGroup
          label="Anzeige"
          size="default"
          value={onlyOpen ? '1' : ''}
          onValueChange={(v) => setSearch({ open: v ? 1 : undefined })}
          options={FILTER_OPTIONS}
        />
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (query.data?.data ?? []).length === 0 ? (
        <EmptyState
          icon={Gauge}
          title="Keine Zähler zur Ablesung"
          description="Ihnen sind derzeit keine Zähler zugeordnet, oder das Ablesejahr wurde noch nicht angelegt. Bei Fragen wenden Sie sich an Ihre Verwaltung."
        />
      ) : groups.length === 0 ? (
        <EmptyState icon={Gauge} title="Keine Treffer" description="Für die Suche bzw. den Filter gibt es keine Zähler." />
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <PropertyGroup key={group.property?.id} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}

function PropertyGroup({ group }) {
  const headingId = useId();
  return (
    <section role="region" aria-labelledby={headingId} data-slot="section" className="flex flex-col overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b bg-muted/40 px-4 py-2.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={headingId} className="text-sm font-semibold">
            {group.property?.name || `Liegenschaft #${group.property?.id}`}
          </h2>
          <p className="text-[0.8125rem] text-muted-foreground">
            {[group.property?.address, `${formatNumber(group.open ?? 0)} offen`].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>
      <ul aria-label={`Zähler ${group.property?.name ?? ''}`.trim()} className="flex flex-col divide-y">
        {group.readings.map((reading) => (
          <ReadingItem key={reading.id} reading={reading} />
        ))}
      </ul>
    </section>
  );
}

function ReadingItem({ reading }) {
  const api = useApi();
  const { queue, entryFor } = useMeterQueue();
  const [open, setOpen] = useState(false);
  const queued = entryFor(reading.id);
  const number = reading.meter?.number || `#${reading.id}`;
  const isRead = reading.status === 'read';

  return (
    <li aria-label={`Zähler ${number}`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-4">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <ItemIcon icon={Gauge} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm">
              <span className="numeric font-medium">{number}</span>
              {reading.meter?.type && <span className="text-muted-foreground"> · {reading.meter.type}</span>}
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              <StatusBadge domain="meterReading" status={reading.status} />
              {queued && !queued.error && (
                <Badge variant="warning">
                  <CloudOff aria-hidden="true" /> Wartet auf Verbindung
                </Badge>
              )}
            </span>
          </span>
          <span className="text-[0.8125rem] text-muted-foreground">
            Vorjahr: <span className="numeric">{meterValue(reading.previous_value)}</span>
            {reading.due_on && <> · abzulesen bis {formatDate(reading.due_on)}</>}
          </span>
          {isRead && (
            <span className="text-[0.8125rem]">
              Erfasst: <span className="numeric font-semibold">{meterValue(reading.value)}</span>
              {reading.read_at && <span className="text-muted-foreground"> am {formatDateTime(reading.read_at)}</span>}
              {reading.is_new_meter && <span className="text-muted-foreground"> · neuer Zähler</span>}
            </span>
          )}
          {(reading.has_photo || reading.has_photo_2) && (
            <span className="flex flex-wrap gap-3 text-[0.8125rem]">
              {[reading.has_photo && 1, reading.has_photo_2 && 2].filter(Boolean).map((slot) => (
                <a
                  key={slot}
                  href={api.portal.meterReadings.photoUrl(reading.id, slot)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-7 items-center gap-1.5 text-(--primary-text) hover:underline"
                >
                  <ImageIcon className="size-4" aria-hidden="true" /> Foto {slot}
                </a>
              ))}
            </span>
          )}
        </div>
      </div>
      {queued?.error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-lg bg-destructive-subtle px-3 py-2 text-[0.8125rem] text-destructive-subtle-foreground sm:order-last sm:basis-full"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            Offline erfasster Wert <span className="numeric">{meterValue(queued.value)}</span> wurde abgelehnt: {queued.error}
          </span>
          <Button size="sm" variant="outline" onClick={() => queue.discard(queued.client_uuid)}>
            Verwerfen
          </Button>
        </div>
      )}
      {reading.status !== 'closed' && (
        <Button
          variant={isRead ? 'outline' : 'default'}
          size="sm"
          className="ml-11 w-fit min-w-24 sm:ml-0"
          onClick={() => setOpen(true)}
          aria-label={`${isRead ? 'Korrigieren' : 'Erfassen'}: Zähler ${number}`}
        >
          {isRead ? 'Korrigieren' : 'Erfassen'}
        </Button>
      )}
      <ResponsiveSheet
        open={open}
        onOpenChange={setOpen}
        title={`Zähler ${number}`}
        description={
          <>
            {reading.meter?.type ? `${reading.meter.type} · ` : ''}Vorjahr {meterValue(reading.previous_value)}
          </>
        }
      >
        <CaptureForm reading={reading} number={number} onDone={() => setOpen(false)} />
      </ResponsiveSheet>
    </li>
  );
}

function CaptureForm({ reading, number, onDone }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { queue } = useMeterQueue();
  const photo1Id = useId();
  const photo2Id = useId();
  const [photos, setPhotos] = useState(/** @type {{ photo: File|null, photo_2: File|null }} */ ({ photo: null, photo_2: null }));
  const form = useForm({ resolver: zodResolver(meterReadingSchema), defaultValues: { value: '', is_new_meter: false } });

  const onSubmit = form.handleSubmit(async (values) => {
    const entry = {
      client_uuid: newClientUuid(),
      reading_id: reading.id,
      value: /** @type {number} */ (parseMeterValue(values.value)),
      is_new_meter: values.is_new_meter,
      photo: photos.photo,
      photo_2: photos.photo_2,
      meter_number: reading.meter?.number ?? null,
      created_at: new Date().toISOString(),
    };
    try {
      await api.portal.meterReadings.record(reading.id, {
        value: entry.value,
        is_new_meter: entry.is_new_meter,
        client_uuid: entry.client_uuid,
        photo: entry.photo,
        photo_2: entry.photo_2,
      });
      await queryClient.invalidateQueries({ queryKey: keys.portal.resource('meter-readings') });
      toast.success(`Zählerstand für ${number} gespeichert`);
      onDone();
    } catch (error) {
      if (isNetworkError(error)) {
        try {
          await queue.enqueue(entry);
          toast.info('Offline gespeichert – wird automatisch übertragen, sobald eine Verbindung besteht.');
          onDone();
        } catch (storeError) {
          form.setError('root.server', {
            type: 'server',
            message: getErrorMessage(storeError, 'Keine Verbindung und der Wert konnte nicht zwischengespeichert werden.'),
          });
        }
        return;
      }
      if (hasProblemType(error, 'meter-year-closed')) {
        form.setError('root.server', {
          type: 'server',
          message: 'Das Ablesejahr ist abgeschlossen – Werte können nicht mehr erfasst werden. Bitte wenden Sie sich an Ihre Verwaltung.',
        });
        return;
      }
      applyProblemToForm(error, form.setError, { fields: ['value', 'is_new_meter'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate aria-label={`Zählerstand erfassen: ${number}`} className="flex flex-col gap-4">
        <FormRootError />
        <div className="flex flex-col gap-4">
          <InputField
            name="value"
            label="Zählerstand"
            inputMode="decimal"
            autoComplete="off"
            autoFocus
            description="Nur Ziffern, Nachkommastellen mit Komma."
          />
          <CheckboxField name="is_new_meter" label="Neuer Zähler" description="Der Zähler wurde getauscht – der Wert gilt für den neuen Zähler." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <PhotoInput id={photo1Id} label="Foto 1" file={photos.photo} onChange={(file) => setPhotos((p) => ({ ...p, photo: file }))} />
          <PhotoInput id={photo2Id} label="Foto 2" file={photos.photo_2} onChange={(file) => setPhotos((p) => ({ ...p, photo_2: file }))} />
        </div>
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
          Speichern
        </Button>
      </form>
    </Form>
  );
}

/** Camera/gallery picker for one photo (optional), as a tappable tile. */
function PhotoInput({ id, label, file, onChange }) {
  return (
    <div className="relative flex min-h-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/30 p-3 text-center transition-colors focus-within:outline-2 focus-within:outline-ring hover:bg-muted/60">
      <Camera className="size-5 text-muted-foreground" aria-hidden="true" />
      <Label htmlFor={id} className="text-[0.8125rem]! font-medium!">
        {label}
      </Label>
      <span className="max-w-full truncate text-xs text-muted-foreground">{file ? file.name : 'Aufnehmen oder wählen'}</span>
      <input
        id={id}
        type="file"
        accept="image/*"
        capture="environment"
        className="absolute inset-0 cursor-pointer opacity-0"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}
